import React, { useMemo, useReducer, useState } from 'react';
import styled, { css } from 'styled-components';
import type { ApolloQueryResult } from '@apollo/client';
import { equals } from 'ramda';
import { Body, Heading3, Heading4 } from '~/components/atom/Typography';
import JustificationContainer from '~/components/atom/JustificationContainer';
import removeTypenames from '~/util/removeTypenames';
import useOffices from '~/hooks/useOffices';
import {
  Lfa_Account_Status,
  useUpdateLfaAccountSettingsMutation,
  type GetLfaAccountSettingsQuery,
  type GetLfaConversionDataQuery,
} from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import Divider from '~/components/atom/Divider';
import Button from '~/components/atom/Button';
import { TEXT } from './constants';
import settingsReducer, {
  type UpdateSettingsState,
} from './reducers/settingsReducer';
import servicesV2Reducer, {
  type ServicesV2State,
} from './reducers/servicesV2Reducer';
import useSettingsValidation from './hooks/useSettingsValidation';
import CallToActionBlock from '~/components/organism/CallToActionBlock';
import SelectServicesSection from './components/SelectServicesSection';
import AccountDescriptionSection from './components/AccountDescriptionSection';
import CallbackPhoneNumberSection from './components/CallbackPhoneNumberSection';
import RegelsGrid from './components/RegelsGrid';
import FlynkSettingsSection from './components/FlynkSettingsSection';
import ServicesV2Section from './components/ServicesV2Section/ServicesV2Section';
import FloatingSaveBar from '~/components/molecule/FloatingSaveBar';
import getUpdatedSettingsInput from './utils/getUpdatedSettingsInput';
import hasValue from '~/util/hasValue';

type Props = {
  queryData: NonNullable<GetLfaAccountSettingsQuery['getLfaAccountSettings']>;
  allServices: GetLfaConversionDataQuery['getLfaConversionData']['allServices'];
  refetch: () => Promise<ApolloQueryResult<GetLfaAccountSettingsQuery>>;
};

const UpdateForm: React.FCC<Props> = ({ queryData, allServices, refetch }) => {
  const addToast = useAddToast();
  const { id: accountId } = useCurrentAccount();
  const contentRef = React.useRef<HTMLDivElement>(null);

  const { description, services, rules, flynck, callbackPhoneNumber } =
    queryData;

  const { notOfficeId, notTags, notUserId, minLeadscore } = rules;
  const excludedEmailDomains = rules.excludedEmailDomains ?? [];

  const initialSettings: UpdateSettingsState = removeTypenames({
    description: description || null,
    services,
    rules: {
      notOfficeId,
      notTags,
      notUserId,
      minLeadscore: minLeadscore.toString(),
      excludedEmailDomains,
    },
    flynck: flynck || null,
    callbackPhoneNumber: callbackPhoneNumber || null,
  });

  const [updateSettings, dispatch] = useReducer(
    settingsReducer,
    initialSettings,
  );

  const initialServicesV2: ServicesV2State = queryData.servicesV2 ?? null;
  const [servicesV2, dispatchV2] = useReducer(
    servicesV2Reducer,
    initialServicesV2,
  );

  const [tagInputValue, setTagInputValue] = useState('');
  const [excludedDomainInputValue, setExcludedDomainInputValue] = useState('');

  const offices = useOffices({ userIds: [] });

  const serviceNames = useMemo(
    () => new Map(allServices.map(s => [s.id, s.name])),
    [allServices],
  );

  const officeIdKey = offices.map(o => o.id).join(',');
  const officeNames = useMemo(
    () => new Map(offices.map(o => [o.id, o.name])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [officeIdKey],
  );

  const [updateLfaAccountSettings, { loading: updateLoading }] =
    useUpdateLfaAccountSettingsMutation();

  const { errors, hasErrors, setFieldTouched, clearAllErrors } =
    useSettingsValidation({
      updateSettings,
      servicesV2,
      serviceNames,
      officeNames,
    });

  const onUpdate = async () => {
    const updatedFields = getUpdatedSettingsInput(updateSettings, servicesV2);

    void updateLfaAccountSettings({
      variables: {
        accountId,
        update: {
          UpdateSettings: updatedFields,
        },
      },
    }).then(async ({ data, errors }) => {
      if (data && !errors) {
        clearAllErrors();
        const { data: refetchedData } = await refetch();
        const newQueryData = refetchedData?.getLfaAccountSettings;
        if (newQueryData) {
          const newExcluded = newQueryData.rules.excludedEmailDomains ?? [];
          dispatch({
            type: 'RESET',
            payload: removeTypenames({
              description: newQueryData.description || null,
              services: newQueryData.services,
              rules: {
                notOfficeId: newQueryData.rules.notOfficeId,
                notTags: newQueryData.rules.notTags,
                notUserId: newQueryData.rules.notUserId,
                minLeadscore: newQueryData.rules.minLeadscore.toString(),
                excludedEmailDomains: newExcluded,
              },
              flynck: newQueryData.flynck || null,
              callbackPhoneNumber: newQueryData.callbackPhoneNumber || null,
            }),
          });
          dispatchV2({
            type: 'V2_RESET',
            payload: newQueryData.servicesV2 ?? null,
          });
        }
        return addToast([formatToastMessage(TEXT.successMessage, 'success')]);
      }
      return addToast([formatToastMessage(TEXT.errorMessage, 'danger')]);
    });
  };

  const isActive = queryData.status === Lfa_Account_Status.Active;
  const isInactive = queryData.status == Lfa_Account_Status.Inactive;

  const saveButtonDisabled = isActive && (updateLoading || hasErrors);

  const hasSettingsChanges = !equals(initialSettings, updateSettings);
  const hasV2Changes = !equals(initialServicesV2, servicesV2);
  const hasChanges = hasSettingsChanges || hasV2Changes;
  const saveBarError = Object.values(errors).find(error =>
    hasValue(error),
  )?.[0];

  const backendHasV2 = initialServicesV2 !== null;

  return (
    <>
      {isInactive && (
        <CallToActionBlock
          header={TEXT.accountNotActiveHeader}
          description={TEXT.accountNotActiveWarning}
          icon={{
            name: 'exclamation',
            background: { group: 'warning' },
          }}
          margin={[null, null, 'base']}
        />
      )}

      <Container
        direction="column"
        width="100%"
        gap="l"
        $disabled={updateLoading || isInactive}
      >
        <Heading3 size="l" margin={[null]}>
          {TEXT.settingsHeader}
        </Heading3>

        <AccountDescriptionSection
          updateSettings={updateSettings}
          dispatch={dispatch}
        />

        <CallbackPhoneNumberSection
          updateSettings={updateSettings}
          phoneNumberErrors={errors.phoneNumber}
          dispatch={dispatch}
          setFieldTouched={setFieldTouched}
        />

        <V1Section $dimmed={backendHasV2}>
          {backendHasV2 && (
            <V1Overlay>
              <Body size="s" margin={[null]} color={{ group: 'tertiary' }}>
                {TEXT.v1DisabledByV2}
              </Body>
            </V1Overlay>
          )}
          <SelectServicesSection
            allServices={removeTypenames(allServices)}
            updateSettings={updateSettings}
            dispatch={dispatch}
            setFieldTouched={setFieldTouched}
          />
        </V1Section>

        <Heading4 size="m">{TEXT.rulesHeader}</Heading4>

        <RegelsGrid
          updateSettings={updateSettings}
          dispatch={dispatch}
          leadScoreErrors={errors.leadscore}
          tagInputValue={tagInputValue}
          setTagInputValue={setTagInputValue}
          excludedDomainInputValue={excludedDomainInputValue}
          setExcludedDomainInputValue={setExcludedDomainInputValue}
          setFieldTouched={setFieldTouched}
        />

        <Divider margin={['s', null]} />

        <FlynkSettingsSection
          setFieldTouched={setFieldTouched}
          flynckErrors={errors.flynck}
          updateSettings={updateSettings}
          dispatch={dispatch}
        />

        <Divider margin={['s', null]} />

        {servicesV2 ? (
          <>
            <ServicesV2Section
              servicesV2={servicesV2}
              allServices={allServices}
              dispatch={dispatchV2}
            />
            <JustificationContainer>
              <Button
                size="small"
                appearance="danger"
                icon="trashcan"
                onClick={() => dispatchV2({ type: 'V2_REMOVE' })}
                label={TEXT.removeServicesV2}
              />
            </JustificationContainer>
          </>
        ) : (
          <JustificationContainer direction="column" gap="s">
            <Heading4 size="m" margin={[null]}>
              {TEXT.servicesV2Header}
            </Heading4>
            <Body size="s" margin={[null]} color={{ group: 'tertiary' }}>
              {TEXT.servicesV2Description}
            </Body>
            <JustificationContainer>
              <Button
                size="small"
                appearance="secondary"
                icon="plus"
                onClick={() =>
                  dispatchV2({
                    type: 'V2_INIT_FROM_V1',
                    payload: { services: updateSettings.services },
                  })
                }
                label={TEXT.activateServicesV2}
              />
            </JustificationContainer>
          </JustificationContainer>
        )}

        <FloatingSaveBar
          visible={hasChanges}
          anchorRef={contentRef}
          errorMessage={saveBarError}
          actionProps={{
            onCancel: () => {
              clearAllErrors();
              dispatch({ type: 'RESET', payload: initialSettings });
              dispatchV2({ type: 'V2_RESET', payload: initialServicesV2 });
            },
            onSave: onUpdate,
            loading: updateLoading,
            disabled: saveButtonDisabled,
          }}
        />
      </Container>
    </>
  );
};

const Container = styled(JustificationContainer)<{ $disabled: boolean }>`
  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

const V1Section = styled.div<{ $dimmed: boolean }>(
  ({ $dimmed }) => css`
    position: relative;
    ${$dimmed &&
    css`
      opacity: 0.4;
      pointer-events: none;
      user-select: none;
    `}
  `,
);

const V1Overlay = styled.div(
  ({ theme }) => css`
    position: absolute;
    top: -${theme.space('xs')};
    left: 0;
    right: 0;
    z-index: 1;
    text-align: center;
  `,
);

export default UpdateForm;
